import {
  AttachInternals,
  Component,
  Element,
  Event,
  EventEmitter,
  h,
  Host,
  Listen,
  Prop,
  State,
  Watch,
} from '@stencil/core'
import type { JSX } from '@stencil/core'

import { CssClassMap } from '../../utils/interfaces'

// Constantes para valores fixos
const DENSITY_SMALL = 'small'
const DENSITY_MEDIUM = 'medium'
const DENSITY_LARGE = 'large'

const SHAPE_CIRCLE = 'circle'
const SHAPE_ROUNDED = 'rounded'
const SHAPE_DEFAULT = 'default'

/**
 * ## Design System
 *
 * Para a documentação completa de design, incluindo diretrizes de uso, acessibilidade e exemplos visuais, consulte o [Design System do GovBR](https://www.gov.br/ds/components/textarea?tag=designer).
 *
 * @slot default - Slot para inserir o conteúdo da tag, como rótulo ou ícone
 */
@Component({
  tag: 'br-tag',
  styleUrl: 'tag.scss',
  shadow: true,
  formAssociated: true,
})
export class Tag {
  /**
   * Referência ao elemento host do componente.
   * Utilize esta propriedade para acessar e manipular o elemento do DOM associado ao componente.
   */
  @Element() el: HTMLBrTagElement

  /** API de controle de formulário nativa */
  @AttachInternals() elementInternals: ElementInternals

  /**
   * A propriedade 'label' é uma string que representa o texto a ser exibido no componente.
   * Ela é refletida no DOM, permitindo que alterações no valor sejam refletidas no elemento HTML correspondente.
   */
  @Prop({ reflect: true }) readonly label: string = ''

  /**
   * A propriedade 'iconName' define o nome do ícone que será exibido ao lado do texto.
   * O valor padrão é 'fa-solid:car', que corresponde a um ícone de carro da biblioteca Font Awesome.
   */
  @Prop({ reflect: true }) readonly iconName: string = ''

  /**
   * A propriedade 'name' é uma string que representa o nome do grupo da tag para seleção.
   * Ela é refletida no DOM, permitindo que alterações no valor sejam refletidas no elemento HTML correspondente.
   */
  @Prop({ reflect: true }) readonly name: string = ''

  /**
   * A tag permite seleção múltipla.
   * */
  @Prop({ reflect: true }) readonly multiple: boolean = false

  /**
   * A tag está selecionada.
   * */
  @Prop({ reflect: true, mutable: true }) selected: boolean = false

  /**
   * Identificador único.
   * Caso não seja fornecido, um ID gerado automaticamente será usado.
   */
  @Prop({ reflect: true }) readonly customId: string = `br-tag-${tagId++}`

  /**
   * A propriedade 'color' permite definir a cor do componente.
   * Aceita valores como 'red', 'blue', entre outros, e é refletida no DOM.
   */
  @Prop({ reflect: true }) readonly bgColor: string = ''

  /**
   * A propriedade 'density' permite definir a densidade do componente.
   */
  @Prop({ reflect: true }) readonly density: 'small' | 'medium' | 'large' = DENSITY_MEDIUM

  /**
   * A propriedade 'shape' define o formato do componente.
   */
  @Prop({ reflect: true }) readonly shape: 'circle' | 'rounded' | 'default' = SHAPE_DEFAULT

  /**
   * Transforma a tag em um indicador de status, com aparência circular.
   * É flexível podendo ser utilizado com label ou apenas a superfície circular (informação é transmitida por meio de cores).
   */
  @Prop({ reflect: true }) readonly status: boolean = false

  /**
   * Interação deve ser habilitada.
   */
  @Prop({ reflect: true, mutable: true }) interaction: boolean = false

  /**
   * Interação de seleção deve ser habilitada.
   * */
  @Prop({ reflect: true }) interactionSelect: boolean = false

  /**
   * Tag deve estar desabilitado.
   */
  @Prop({ reflect: true }) readonly disabled: boolean = false

  /**
   * Rótulo personalizado para leitores de tela.
   * Quando fornecido, substitui completamente o texto visível para usuários de leitores de tela.
   * Útil quando o texto visível não é suficientemente descritivo (ex: ícones, abreviações).
   */
  @Prop({ reflect: true }) readonly ariaLabel: string = null

  /**
   * ID do elemento que fornece descrição adicional da tag.
   * Referencia um elemento na página que contém informações complementares sobre a tag.
   * A descrição é lida após o conteúdo principal, fornecendo contexto extra aos usuários de leitores de tela.
   * Exemplo: aria-describedby="help-status" onde existe <div id="help-status">Aguardando aprovação</div>
   */
  @Prop({ reflect: true }) readonly ariaDescribedby: string = null

  /**
   * Tag está selecionada.
   */
  @State() selectedClassValue: boolean = false

  /**
   * Evento emitido quando a tag é selecionada.
   */
  @Event() radioSelected: EventEmitter<string>

  @Watch('interaction')
  handleInteractionChange(newValue: boolean) {
    if (newValue && this.interactionSelect) {
      console.warn(
        "Aviso: 'interaction' e 'interactionSelect' não podem ser usados ao mesmo tempo. Alterando o 'interaction' para 'false'."
      )
      this.interaction = false
    }
  }

  @Watch('interactionSelect')
  handleInteractionSelectChange(newValue: boolean) {
    if (newValue && this.interaction) {
      console.warn(
        "Aviso: 'interaction' e 'interactionSelect' não podem ser usados ao mesmo tempo. Alterando o 'interaction' para 'false'."
      )
      this.interaction = false
    }
  }

  @Watch('selected')
  handleSelectedChange(newValue: boolean) {
    this.selectedClassValue = newValue
  }

  @Listen('radioSelected', { target: 'document' })
  handleCheckedChange(event: CustomEvent) {
    if (event.detail === this.name && this.el !== event.target) {
      this.selected = false
    }
  }

  componentWillLoad() {
    if (this.interaction && this.interactionSelect) {
      console.warn(
        "Aviso: 'interaction' e 'interactionSelect' não podem ser usados ao mesmo tempo. Alterando o 'interaction' para 'false'."
      )
      this.interaction = false
    }
  }
  connectedCallback() {
    this.selectedClassValue = this.selected
    this.el.addEventListener('keydown', this.handleKeyDown)
  }

  disconnectedCallback() {
    this.el.removeEventListener('keydown', this.handleKeyDown)
  }
  /**
   * Permite navegação por teclado (setas) entre tags do tipo radio (seleção unica).
   * Funciona apenas quando 'interactionSelect' é true e 'multiple' é false.
   * Ignora tags desabilitadas.
   * @param event - Evento de teclado
   */
  private handleKeyDown = (event: KeyboardEvent) => {
    if (!this.interactionSelect || this.multiple || this.disabled) return

    if (event.key === 'ArrowRight') {
      event.preventDefault()
      this.selectNextRadio()
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault()
      this.selectPreviousRadio()
    }
  }

  private selectNextRadio() {
    this.selectRadioByStep(1)
  }

  private selectPreviousRadio() {
    this.selectRadioByStep(-1)
  }

  private selectRadioByStep(step: number) {
    const radios = Array.from(
      document.querySelectorAll(`br-tag[interaction-select][name="${this.name}"]:not([disabled])`)
    ) as HTMLElement[]
    const currentIndex = radios.indexOf(this.el)
    if (currentIndex === -1) return
    const newIndex = (currentIndex + step + radios.length) % radios.length
    this.selectRadioByIndex(radios, newIndex)
  }

  private selectRadioByIndex(radios: HTMLElement[], index: number) {
    const nextRadio = radios[index] as HTMLBrTagElement
    const input = nextRadio.shadowRoot?.querySelector('input[type="radio"]') as HTMLInputElement | null
    if (input !== null) {
      input.focus()
      input.click()
    }
  }

  private getCssClassMap(): CssClassMap {
    return {
      'br-tag': true,
      interaction: this.interaction,
      'interaction-select': this.interactionSelect,
      selected: this.selectedClassValue,
      disabled: this.disabled,
      [DENSITY_SMALL]: this.density === DENSITY_SMALL,
      [DENSITY_MEDIUM]: this.density === DENSITY_MEDIUM,
      [DENSITY_LARGE]: this.density === DENSITY_LARGE,
      count: this.shape === SHAPE_ROUNDED,
      icon: this.shape === SHAPE_CIRCLE,
      status: this.status,
    }
  }

  private getTagAttributes() {
    if (this.interaction) {
      return {
        id: this.customId,
      }
    }

    return {
      id: this.customId,
      role: this.getRole(),
      'aria-label': this.getAriaLabel(),
      'aria-describedby': this.getAriaDescribedBy(),
      'aria-checked': this.getAriaChecked(),
      'aria-disabled': this.getAriaDisabled(),
    }
  }

  private getAriaDescribedBy(): string | null {
    const hostTitle = this.el.getAttribute('title')
    if (this.hasContent(hostTitle)) {
      return null
    }

    const hasInternalLabel = !this.status && this.label !== ''

    if (this.ariaDescribedby && this.ariaDescribedby.trim() !== '') {
      return this.ariaDescribedby
    }

    if (hasInternalLabel && !this.hasContent(this.ariaLabel)) {
      return `${this.customId}-label`
    }

    return null
  }

  /**
   * Verifica se uma string possui conteúdo válido (não vazia após remover espaços em branco).
   * @param value - String a ser verificada
   * @returns {boolean} true se a string possui conteúdo
   */
  private hasContent(value: string): boolean {
    return value?.trim().length > 0
  }

  private getAriaLabel(): string | null {
    return this.ariaLabel && this.ariaLabel.trim() !== '' ? this.ariaLabel : null
  }

  private getRole(): string | null {
    const hostTitle = this.el.getAttribute('title')
    if (this.hasContent(hostTitle)) {
      return null
    }

    if (this.interactionSelect) return this.multiple ? 'checkbox' : 'radio'
    return 'status'
  }

  private getAriaChecked(): string | null {
    return this.interactionSelect ? (this.selected ? 'true' : 'false') : null
  }

  private getAriaDisabled(): string | null {
    return this.disabled ? 'true' : null
  }

  private renderIcon() {
    return this.iconName && this.iconName.trim() !== '' ? (
      <br-icon icon-name={this.iconName} aria-hidden="true" />
    ) : null
  }

  private renderLabel() {
    return this.shape !== SHAPE_CIRCLE && !this.status ? <span id={`${this.customId}-label`}>{this.label}</span> : null
  }

  private renderCloseButton() {
    if (!this.interaction) return null
    return (
      <br-button
        shape="circle"
        emphasis="primary"
        density="small"
        aria-label={`Fechar ${this.label || 'tag'}`}
        onClick={this.handleClose}
        disabled={this.disabled}
      >
        <br-icon icon-name="fa-solid:times" aria-hidden="true"></br-icon>
      </br-button>
    )
  }

  private renderStatusLabel() {
    return this.status ? <span class="tag-spacing-status">{this.label}</span> : null
  }

  private handleClose = () => {
    this.el.remove()
  }

  private handleChangeValueChecked = () => {
    if (!this.multiple) {
      this.radioSelected.emit(this.name)
    }
    this.selected = !this.selected
  }

  private handleChangeRadioValue = (event: MouseEvent) => {
    if (!this.multiple) {
      this.radioSelected.emit(this.name)
      this.selected = (event.target as HTMLInputElement).checked
    }
  }

  private renderMainTagContent() {
    return (
      <span
        class={this.getCssClassMap()}
        {...this.getTagAttributes()}
        style={{ backgroundColor: this.bgColor || undefined }}
      >
        {this.renderIcon()}
        {this.renderLabel()}
        {this.renderCloseButton()}
        <slot></slot>
      </span>
    )
  }

  private renderStatusLayout(content: JSX.Element) {
    return (
      <span style={{ display: 'flex', alignItems: 'center' }}>
        {content}
        {this.renderStatusLabel()}
      </span>
    )
  }

  private handleInputChange = (event: MouseEvent) => {
    if (this.multiple) {
      this.handleChangeValueChecked()
    } else {
      this.handleChangeRadioValue(event)
    }
  }

  private renderInteractionSelectTag() {
    if (!this.interactionSelect) {
      return null
    }

    return (
      <span class={this.getCssClassMap()} id={this.customId}>
        <input
          id={`input-${this.customId}`}
          type={this.multiple ? 'checkbox' : 'radio'}
          name={this.name}
          value={this.label}
          checked={this.selected}
          onClick={this.handleInputChange}
          disabled={this.disabled}
        />
        <label htmlFor={`input-${this.customId}`}>
          {this.renderIcon()}
          {this.renderLabel()}
        </label>
      </span>
    )
  }

  render() {
    const mainContent = this.renderMainTagContent()

    return (
      <Host>
        {this.interactionSelect
          ? this.renderInteractionSelectTag()
          : this.status
            ? this.renderStatusLayout(mainContent)
            : mainContent}
      </Host>
    )
  }
}

let tagId = 0
